import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { useFamily } from "@/hooks/useFamily";
import { getChildSession } from "@/lib/device";
import { RoleDebugBanner } from "@/components/shared/RoleDebugBanner";
import { PwaUpdatedToast } from "@/components/PwaUpdatedToast";

import { AppShell } from "@/components/AppShell";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Onboarding from "./pages/Onboarding";
import InviteAccept from "./pages/InviteAccept";
import Index from "./pages/Index";
import ParentChildView, { ParentChildPreview } from "./pages/parent/ParentChildView";
import ChildCalendar from "./pages/parent/ChildCalendar";
import ChildDayView from "./pages/child/ChildDayView";
import Custody from "./pages/Custody";
import Exchange from "./pages/Exchange";
import Reports from "./pages/Reports";
import Settings from "./pages/Settings";
import InviteCoparent from "./pages/InviteCoparent";
import ChecklistTemplateEditorPage from "./pages/ChecklistTemplateEditor";
import ChildAccess from "./pages/ChildAccess";

import PwaEntry from "./pages/PwaEntry";
import StartEntry from "./pages/StartEntry";
import NotFound from "./pages/NotFound";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import LiteRedirect from "./pages/LiteRedirect";

const queryClient = new QueryClient();

/** Guards: unauthenticated → /login, no family → /onboarding */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const { hasFamily, isLoading: famLoading, isError: famError } = useFamily();

  if (authLoading || famLoading) {
    return null; // AppShell handles the visual loading state
  }

  if (!user) return <Navigate to="/" replace />;
  // If the family query errored (e.g. expired token), don't redirect to onboarding
  if (famError) return null;
  if (!hasFamily) return <Navigate to="/onboarding" replace />;

  return <>{children}</>;
}

/** Redirect authenticated users away from login/register */
function PublicOnlyRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const { hasFamily, isLoading, isError: famError } = useFamily();

  // Child device? Send straight to child access handler
  const childSession = getChildSession();
  if (childSession) return <Navigate to="/child/access" replace />;

  if (loading || isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <p className="text-muted-foreground">Laster…</p>
      </div>
    );
  }

  // Don't redirect if family query failed – stay on current page
  if (user && famError) return <>{children}</>;
  if (user && hasFamily) return <Navigate to="/dashboard" replace />;
  if (user && !hasFamily) return <Navigate to="/onboarding" replace />;

  return <>{children}</>;
}

/** Onboarding: authenticated users can always reach this to create a new family */
function OnboardingRoute({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const { isLoading: famLoading } = useFamily();

  if (authLoading || famLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <p className="text-muted-foreground">Laster…</p>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <RoleDebugBanner />
          <PwaUpdatedToast />
          <Routes>
            {/* ── Public routes ────────────────────── */}
            <Route path="/" element={<PublicOnlyRoute><Landing /></PublicOnlyRoute>} />
            <Route path="/login" element={<PublicOnlyRoute><Login /></PublicOnlyRoute>} />
            <Route path="/register" element={<PublicOnlyRoute><Register /></PublicOnlyRoute>} />
            <Route path="/forgot-password" element={<PublicOnlyRoute><ForgotPassword /></PublicOnlyRoute>} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/onboarding" element={<OnboardingRoute><Onboarding /></OnboardingRoute>} />
            <Route path="/invite/:code" element={<InviteAccept />} />
            <Route path="/personvern" element={<Privacy />} />
            <Route path="/vilkar" element={<Terms />} />
            <Route path="/start" element={<StartEntry />} />
            <Route path="/pwa" element={<PwaEntry />} />
            <Route path="/lite" element={<LiteRedirect />} />

            {/* ── Parent routes: wrapped by AppShell (centralized loading) ── */}
            <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
              <Route path="/dashboard" element={<Index />} />
              <Route path="/parent/child/:id" element={<ParentChildView />} />
              <Route path="/parent/child/:id/preview" element={<ParentChildPreview />} />
              <Route path="/parent/child/:id/calendar" element={<ChildCalendar />} />
              <Route path="/custody" element={<Custody />} />
              <Route path="/exchange" element={<Exchange />} />
              <Route path="/reports" element={<Reports />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/settings/invite-coparent" element={<InviteCoparent />} />
              <Route path="/settings/templates" element={<ChecklistTemplateEditorPage />} />
              <Route path="/settings/templates/:childId" element={<ChecklistTemplateEditorPage />} />
            </Route>

            {/* ── Child routes (device session) ────── */}
            <Route path="/child/access" element={<ChildAccess />} />
            <Route path="/child/claim" element={<Navigate to="/pwa" replace />} />
            <Route path="/child/:id" element={<ChildDayView />} />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
